/**
 * API Error Handler Utility
 * 
 * Extracts user-friendly error messages from API error responses
 * Handles different HTTP status codes appropriately
 */

interface ApiErrorResponse {
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      errorMessage?: string;
    };
  };
  request?: unknown;
}

/**
 * Extract error message from API error response
 * @param error - The error object from axios catch block
 * @returns User-friendly error message string
 */
export const getApiErrorMessage = (error: unknown): string => { // NOSONAR
  const apiError = error as ApiErrorResponse;

  // Check if it's a response error (server responded)
  if (apiError?.response) {
    const status = apiError.response.status;
    const errorData = apiError.response.data;

    // Handle 403 Forbidden - Access denied
    if (status === 403) {
      // Use the message from backend if available
      const backendMessage = errorData?.message || errorData?.errorMessage || errorData?.error;
      if (backendMessage) {
        return backendMessage;
      }
      return "You do not have access to this member's data";
    }

    // Handle 404 Not Found
    if (status === 404 || status === 400) {
      return "No records were found for this patient.";
    }

    // Handle 5xx Server Errors
    if (status && status >= 500) {
      return "A network issue occurred while fetching data.";
    }

    // Handle other 4xx errors (401, 402, etc.)
    if (status && status >= 400) {
      const backendMessage = errorData?.message || errorData?.errorMessage || errorData?.error;
      if (backendMessage) {
        return backendMessage;
      }
      return "A network issue occurred while fetching data.";
    }
  }

  // Handle request errors (no response from server)
  if (apiError?.request) {
    return "A network issue occurred while fetching data.";
  }

  // Default fallback
  return "A network issue occurred while fetching data.";
};

/**
 * Check if an error message should be displayed in red (error/warning state)
 * @param error - The error object from axios catch block
 * @returns true if error should be shown in red
 */
export const isErrorRed = (error: unknown): boolean => {
  const apiError = error as ApiErrorResponse;

  // Check if it's a response error (server responded)
  if (apiError?.response) {
    const status = apiError.response.status;

    // 403 Forbidden - Access denied (show in red)
    if (status === 403) {
      return true;
    }

    // 5xx Server Errors (show in red)
    if (status && status >= 500) {
      return true;
    }

    // Other 4xx errors that aren't "not found" (show in red)
    if (status && status >= 400 && status !== 404) {
      return true;
    }
  }

  // Request errors (no response from server) - show in red
  if (apiError?.request) {
    return true;
  }

  return false;
};

/**
 * Get error message and determine if it should be displayed in red
 * @param error - The error object from axios catch block
 * @returns Object with message and isRed flag
 */
export const getApiErrorWithStyle = (error: unknown): { message: string; isRed: boolean } => {
  return {
    message: getApiErrorMessage(error),
    isRed: isErrorRed(error),
  };
};

